/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ContentType } from '../components-v3/content-builder/add-content-dialog/add-content-dialog.model';
import { NestedEntityType } from './nested-path.helper';

/**
 * The fields a section item can carry its kind in. `type` is the card's own type, while
 * `v3CardType` and `collectionType` describe what a `PLAYLIST` card actually is — an indicator, for
 * instance, arrives as `type: 'PLAYLIST'` with `collectionType: 'PERSONALITY_INDICATOR'`.
 */
export interface ContentEntityTypeSource {
  type?: string;
  v3CardType?: string;
  collectionType?: string;
}

/**
 * Resolves the entity a content card points at, which is what `NestedPathHelper` needs to build its
 * URL. Kept in one place because every surface showing cards (programme, course, collection,
 * callouts, My Hub) has to agree: a mismatch sends the learner to a path nothing routes.
 */
export function resolveContentEntityType(card: ContentEntityTypeSource): NestedEntityType {
  const cardType = card.type?.toUpperCase();
  const subType = (card.v3CardType || card.collectionType)?.toUpperCase();

  if (cardType === 'ASSIGNMENT' || subType === 'ASSIGNMENT') {
    return 'assignment';
  }
  if (cardType === 'EVENT' || subType === 'EVENT') {
    return 'event';
  }
  // Checked before CARD: an assessment can arrive as type CARD with an ASSESSMENT sub-type.
  if (cardType === 'ASSESSMENT' || subType === 'ASSESSMENT') {
    return 'assessment';
  }
  // `RESOURCE` is what older content calls a card — the personality indicator's report pages still
  // hold theirs that way — and it routes to the same card view. A web link is a card too: the
  // learner surfaces open its link instead, but its editor and members pages live under `cards`.
  if (cardType === 'CARD' || cardType === 'RESOURCE' || cardType === 'WEBLINK' || subType === 'WEBLINK') {
    return 'card';
  }
  if (subType === 'PERSONALITY_INDICATOR') {
    return 'indicator';
  }
  if (subType === 'COLLECTION') {
    return 'collection';
  }
  if (subType === 'PROGRAM' || subType === 'MODULE') {
    return 'program';
  }
  return 'course';
}

/**
 * The editor's own vocabulary for a card's kind, mapped onto the URL grammar. Section cards know
 * their `ContentType` and never see the API payload `resolveContentEntityType` reads.
 */
export function contentTypeToNestedEntity(type: ContentType | null | undefined): NestedEntityType {
  switch (type) {
    case 'program':
      return 'program';
    case 'collection':
      return 'collection';
    case 'card':
      return 'card';
    case 'event':
      return 'event';
    case 'assessment':
      return 'assessment';
    default:
      return 'course';
  }
}

/**
 * Playlists are referenced by their own uid, everything else as a card inside the parent playlist.
 * The section payload and the library search both turn on this split.
 */
export function isPlaylistContentType(type: ContentType | null | undefined): boolean {
  return type === 'course' || type === 'program' || type === 'collection';
}
